import { nextTick, ref, toRef, toValue, watch } from 'vue'
import type { MaybeRefOrGetter } from 'vue'
import { projectApi } from '@/services/api'
import { useLocalStorage } from '@vueuse/core'

export const addToLastAccessedProjects = (project) => {
  if (!project?._id)
    return
  const accessedProjects = useLocalStorage('lastAccessedProjects', [])
  const foundProject = accessedProjects.value.findIndex(currentProject => currentProject._id === project._id)
  if (foundProject === -1) {
    accessedProjects.value.unshift({
      _id: project._id,
      display: project.display,
      startDate: project.startDate,
      endDate: project.endDate,
      isLocked: project.isLocked,
      client: project?.client ? { _id: project.client._id, display: project.client.display } : null,
    })
  }
  else {
    // change order of project, insert at the begining
    accessedProjects.value.splice(foundProject, 1)
    accessedProjects.value.unshift({
      _id: project._id,
      display: project.display,
      startDate: project.startDate,
      endDate: project.endDate,
      isLocked: project.isLocked,
      client: project?.client ? { _id: project.client._id, display: project.client.display } : null,
    })
  }

  // only keep last 5 items
  if (accessedProjects.value.length > 5)
    accessedProjects.value.splice(5, accessedProjects.value.length - 5)

  return accessedProjects
}

export const useInfiniteProjectTimesheet = ({ value, limit = ref(30), page = ref(0), projectIds }) => {
  // <b-autocomplete-infity class="w-full" @get-more-data="getMoreResources" :list="resourceAutoComplete.list"
  // :selected="project.contributors" @add-selected="addSalesContributor"
  // @remove-selected="removeSalesContributor"
  // :haveMore="resourceAutoComplete.haveMore && !resourceAutoComplete.loading">
  // <template #custom-list="{ selectItem, list }">
  const list = ref([])
  const haveMore = ref(true)
  const loading = ref(false)

  const getMoreData = async (options) => {
    if (options.new) {
      page.value = 0
      haveMore.value = true
    }
    loading.value = true
    const projectRequest = await projectApi.showProjectTimesheet({ page: page.value, limit: limit.value, display: options.search, projectIds, currentStatus: 0 }).promise
    loading.value = false
    haveMore.value = (projectRequest.count?.currentPage || 1) < (projectRequest.count?.totalPages || 1)
    list.value = options.new ? projectRequest.docs : [...list.value, ...projectRequest.docs]
    page.value++
  }

  const remove = () => {
    value = null
  }

  return {
    haveMore,
    getMoreData,
    remove,
    list,
    value,
  }
}


export const useInfinityProjectExpense = ({ search = ref(''), limit = ref(20), page = ref(0), isLoading = ref(false), list = ref([]) }) => {
  const haveMore = ref(true)
  const getMoreData = async () => {
    try {
      isLoading.value = true

      const searchRequest = projectApi.availableForExpense({ search: search.value, limit: limit.value, page: page.value })
      const searchResponse = await searchRequest.promise
      haveMore.value = searchResponse.count.hasNextPage

      list.value = page.value === 0 ? searchResponse.docs : [...list.value, ...searchResponse.docs]

      return searchResponse
    }
    catch (err) {
      console.log('err', err)
    }
    finally {
      isLoading.value = false
    }
  }

  return {
    search,
    limit,
    page,
    haveMore,
    getMoreData,
    list,
    isLoading,
  }
}


export const useInfinityProject = ({
  search = ref(''),
  limit = ref(20),
  page = ref(0),
  isLoading = ref(false),
  list = ref([]),
  projectIds = ref([]),
  clientId,
  projectTarget,
}: {
  search?: any
  limit?: any
  page?: any
  isLoading?: any
  list?: any
  projectIds?: any
  clientId?: MaybeRefOrGetter<string | null | undefined>
  projectTarget?: MaybeRefOrGetter<string | null | undefined>
} = {}) => {
  const haveMore = ref(true)
  const clientIdRef = clientId ? toRef(clientId) : ref(null)

  if (clientId) {
    watch(clientIdRef, () => {
      search.value = ''
      page.value = 0
      haveMore.value = true
      list.value = []
    })
  }

  const getMoreData = async () => {
    const currentClientId = clientId ? toValue(clientId) : null
    const currentProjectTarget = projectTarget ? toValue(projectTarget) : null

    if (clientId && !currentClientId) {
      list.value = []
      haveMore.value = false
      return
    }

    try {
      isLoading.value = true;

      const searchRequest = projectApi.filter({
        search: search.value,
        limit: limit.value,
        page: page.value,
        currentStatus: 0,
        projectIds: projectIds.value,
        ...(currentClientId && { clientId: currentClientId }),
        ...(currentProjectTarget && { projectTarget: currentProjectTarget }),
      })
      const searchResponse = await searchRequest.promise
      haveMore.value = searchResponse.count.hasNextPage

      await nextTick() // to be sure have more is updated so the request won't be done again

      list.value = page.value === 0 ? searchResponse.docs : [...list.value, ...searchResponse.docs]

      return searchResponse
    }
    catch (err) {
      console.log('err', err)
    }
    finally {
      isLoading.value = false;
    }
  }
  
  return {
    search,
    limit,
    page,
    haveMore,
    getMoreData,
    list,
    isLoading,
  }
}

export const useInfinityProjectTimesheet = ({ search = ref(''), limit = ref(20), page = ref(0), isLoading = ref(false), list = ref([]), resourceId, projectIds = ref([]) }) => {
  const haveMore = ref(true)
  const getMoreData = async () => {
    try {
      isLoading.value = true;

      const searchRequest = projectApi.showProjectTimesheet({ display: search.value, limit: limit.value, page: page.value, currentStatus: 0, projectIds: projectIds.value, resourceId})
      const searchResponse = await searchRequest.promise
      haveMore.value = searchResponse.count.hasNextPage

      await nextTick() // to be sure have more is updated so the request won't be done again

      list.value = page.value === 0 ? searchResponse.docs : [...list.value, ...searchResponse.docs]

      return searchResponse
    }
    catch (err) {
      console.log('err', err)
    }
    finally {
      isLoading.value = false;
    }
  }
  
  return {
    search,
    limit,
    page,
    haveMore,
    getMoreData,
    list,
    isLoading,
  }
}
